import { io, type Socket } from 'socket.io-client';
import type {
  DigPatch,
  RoomJoinedPayload,
  SessionPlayer,
  StageEnterPacket,
  WorldSnapshot,
  WorldSummary,
} from './protocol';

export type MultiplayerRole = 'host' | 'client';

export interface MultiplayerSession {
  code: string;
  role: MultiplayerRole;
  snapshot: WorldSnapshot;
  players: SessionPlayer[];
  playerId: string;
  displayName: string;
  socket: Socket;
}

type SessionHandlers = {
  onPlayerJoined?: (player: SessionPlayer) => void;
  onPlayerLeft?: (playerId: string) => void;
  onStageEnter?: (packet: StageEnterPacket) => void;
  onError?: (message: string) => void;
  onDisconnect?: (reason: string) => void;
};

function serverBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_MULTIPLAYER_URL as string | undefined;
  if (fromEnv && fromEnv.length > 0) return fromEnv.replace(/\/$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return 'http://localhost:3001';
}

export async function fetchWorldList(): Promise<WorldSummary[]> {
  const res = await fetch(`${serverBaseUrl()}/api/worlds`);
  if (!res.ok) throw new Error(`Failed to load worlds (${res.status})`);
  const data = (await res.json()) as { worlds: WorldSummary[] };
  return data.worlds ?? [];
}

export async function deleteWorld(code: string): Promise<void> {
  const res = await fetch(`${serverBaseUrl()}/api/worlds/${code}`, { method: 'DELETE' });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}

function connectSocket(): Socket {
  return io(serverBaseUrl(), {
    transports: ['websocket', 'polling'],
    autoConnect: true,
  });
}

function waitForJoined(socket: Socket): Promise<RoomJoinedPayload> {
  return new Promise((resolve, reject) => {
    const onJoined = (payload: RoomJoinedPayload) => {
      cleanup();
      resolve(payload);
    };
    const onError = (payload: { message: string }) => {
      cleanup();
      reject(new Error(payload.message));
    };
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('Timed out waiting for the multiplayer server.'));
    }, 12000);
    const cleanup = () => {
      window.clearTimeout(timer);
      socket.off('room:joined', onJoined);
      socket.off('room:error', onError);
    };
    socket.on('room:joined', onJoined);
    socket.on('room:error', onError);
  });
}

function bindSessionHandlers(socket: Socket, handlers: SessionHandlers): void {
  if (handlers.onPlayerJoined) {
    socket.on('room:player-joined', handlers.onPlayerJoined);
  }
  if (handlers.onPlayerLeft) {
    socket.on('room:player-left', (p: { playerId: string }) => handlers.onPlayerLeft?.(p.playerId));
  }
  if (handlers.onStageEnter) {
    socket.on('stage:enter', handlers.onStageEnter);
  }
  if (handlers.onError) {
    socket.on('room:error', (p: { message: string }) => handlers.onError?.(p.message));
  }
  if (handlers.onDisconnect) {
    socket.on('disconnect', (reason: string) => handlers.onDisconnect?.(reason));
  }
}

export async function createAndHostWorld(options: {
  name: string;
  seed?: number;
  playerId: string;
  displayName: string;
  handlers?: SessionHandlers;
}): Promise<MultiplayerSession> {
  const socket = connectSocket();
  const joinedPromise = waitForJoined(socket);
  socket.emit('room:create', {
    name: options.name,
    seed: options.seed,
    playerId: options.playerId,
    displayName: options.displayName,
  });
  const joined = await joinedPromise;
  if (options.handlers) bindSessionHandlers(socket, options.handlers);
  return {
    code: joined.code,
    role: 'host',
    snapshot: joined.snapshot,
    players: joined.players,
    playerId: options.playerId,
    displayName: options.displayName,
    socket,
  };
}

export async function hostExistingWorld(options: {
  code: string;
  playerId: string;
  displayName: string;
  handlers?: SessionHandlers;
}): Promise<MultiplayerSession> {
  const socket = connectSocket();
  const joinedPromise = waitForJoined(socket);
  socket.emit('room:host', {
    code: options.code,
    playerId: options.playerId,
    displayName: options.displayName,
  });
  const joined = await joinedPromise;
  if (options.handlers) bindSessionHandlers(socket, options.handlers);
  return {
    code: joined.code,
    role: 'host',
    snapshot: joined.snapshot,
    players: joined.players,
    playerId: options.playerId,
    displayName: options.displayName,
    socket,
  };
}

export async function joinWorld(options: {
  code: string;
  playerId: string;
  displayName: string;
  handlers?: SessionHandlers;
}): Promise<MultiplayerSession> {
  const socket = connectSocket();
  const joinedPromise = waitForJoined(socket);
  socket.emit('room:join', {
    code: options.code,
    playerId: options.playerId,
    displayName: options.displayName,
  });
  const joined = await joinedPromise;
  if (options.handlers) bindSessionHandlers(socket, options.handlers);
  return {
    code: joined.code,
    role: 'client',
    snapshot: joined.snapshot,
    players: joined.players,
    playerId: options.playerId,
    displayName: options.displayName,
    socket,
  };
}

export function saveWorldSnapshot(
  session: MultiplayerSession,
  snapshot: {
    name?: string;
    seed: number;
    runId: number;
    stage: number;
    digs: DigPatch[];
  },
): void {
  session.socket.emit('world:save', {
    code: session.code,
    snapshot,
  });
}

export function emitStageEnter(session: MultiplayerSession, packet: StageEnterPacket): void {
  session.socket.emit('stage:enter', packet);
}

export { serverBaseUrl };
